import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static targets = [
    "progressBar",
    "progressText",
    "currentStep",
    "stats",
    "status",
    "downloadLink",
    "errorMessage",
    "progressSection",
    "currentFileSection",
    "currentFileName",
    "currentFileProgress",
  ];

  static values = {
    transferId: Number,
    accountId: Number,
  };

  connect() {
    console.log(
      "ContentTransferController connected, transfer ID:",
      this.transferIdValue,
      "account ID:",
      this.accountIdValue
    );
    this.createSubscription();
  }

  disconnect() {
    console.log("ContentTransferController disconnecting");
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  createSubscription() {
    const controller = this;

    this.subscription = consumer.subscriptions.create(
      { channel: "ContentTransferChannel", account_id: this.accountIdValue },
      {
        connected() {
          console.log("Connected to ContentTransferChannel");
        },

        disconnected() {
          console.log("Disconnected from ContentTransferChannel");
        },

        received(data) {
          console.log("Received data from ContentTransferChannel:", data);
          controller.handleReceived(data);
        },
      }
    );
  }

  handleReceived(data) {
    // Only process updates for the current transfer
    if (this.hasTransferIdValue && data.transfer_id !== this.transferIdValue) {
      console.log(
        "Ignoring update for different transfer:",
        data.transfer_id,
        "vs",
        this.transferIdValue
      );
      return;
    }

    console.log("Processing update for transfer:", data.transfer_id);
    this.updateProgress(data);
    this.updateStatus(data);
    this.updateStats(data);
    this.updateDownloadLink(data);
    this.updateErrorMessage(data);

    // Reload page when completed or failed to show final state
    if (data.status === "completed" || data.status === "failed") {
      console.log("Transfer finished, reloading page in 1 second");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  updateProgress(data) {
    const progress = data.progress || 0;

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`;
      this.progressBarTarget.setAttribute("aria-valuenow", progress);
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${progress}%`;
    }

    if (this.hasCurrentStepTarget && data.current_step_display) {
      this.currentStepTarget.textContent = data.current_step_display;
    }

    // Update current file info for asset uploads
    this.updateCurrentFile(data);
  }

  updateCurrentFile(data) {
    if (!this.hasCurrentFileSectionTarget) return;

    const stats = data.stats || {};
    const assetStats = stats.assets || {};

    // Show current file section only during asset upload
    if (data.current_step === "assets" && assetStats.current_file) {
      this.currentFileSectionTarget.classList.remove("hidden");

      if (this.hasCurrentFileNameTarget) {
        this.currentFileNameTarget.textContent = assetStats.current_file;
      }

      if (this.hasCurrentFileProgressTarget) {
        const current = assetStats.current || 0;
        const total = assetStats.total || 0;
        this.currentFileProgressTarget.textContent = `(${current} of ${total})`;
      }
    } else {
      this.currentFileSectionTarget.classList.add("hidden");
    }
  }

  updateStatus(data) {
    if (!this.hasStatusTarget) return;

    const status = data.status;
    this.statusTarget.innerHTML = this.statusBadge(status);
  }

  updateStats(data) {
    if (!this.hasStatsTarget || !data.stats) return;

    const stats = data.stats;
    let html = "";

    // Sourced from the server's step registry (ContentTransfer::EXPORT_STEPS /
    // IMPORT_STEPS / RESET_STEPS via #steps), broadcast on every update. Falls back to
    // the export/import step list for older cached payloads without it.
    const steps = data.steps || [
      "templates",
      "blueprints",
      "collections",
      "globals",
      "entries",
      "navigations",
      "forms",
      "assets",
    ];

    steps.forEach((step) => {
      if (stats[step]) {
        const stepStats = stats[step];
        const count = stepStats.count || stepStats.imported || 0;
        const icon = this.getStepIcon(step, data.current_step);

        // Build extra info for assets
        let extraInfo = "";
        if (step === "assets") {
          const parts = [];
          if (stepStats.new > 0) {
            parts.push(`${stepStats.new} new`);
          }
          if (stepStats.replaced > 0) {
            parts.push(`${stepStats.replaced} replaced`);
          }
          if (stepStats.unchanged > 0) {
            parts.push(`${stepStats.unchanged} unchanged`);
          }
          if (stepStats.errors > 0) {
            parts.push(`${stepStats.errors} errors`);
          }
          if (parts.length > 0) {
            extraInfo = `<p class="text-xs text-gray-500">${parts.join(", ")}</p>`;
          }
        }

        html += `
          <div class="bg-white p-3 rounded-lg border border-gray-200">
            <div class="flex items-center gap-2">
              ${icon}
              <span class="text-sm text-gray-600 capitalize">${step}</span>
            </div>
            <p class="mt-1 text-lg font-semibold text-gray-900">${count}</p>
            ${extraInfo}
          </div>`;
      }
    });

    this.statsTarget.innerHTML = html;
  }

  updateDownloadLink(data) {
    if (!this.hasDownloadLinkTarget) return;

    if (data.status === "completed" && data.download_url) {
      this.downloadLinkTarget.href = data.download_url;
      this.downloadLinkTarget.classList.remove("hidden");
    }
  }

  updateErrorMessage(data) {
    if (!this.hasErrorMessageTarget) return;

    if (data.status === "failed" && data.error_message) {
      this.errorMessageTarget.textContent = data.error_message;
      this.errorMessageTarget.classList.remove("hidden");
    }
  }

  statusBadge(status) {
    const classes = {
      pending: "bg-gray-100 text-gray-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    const labels = {
      pending: "Pending",
      processing: "Processing",
      completed: "Completed",
      failed: "Failed",
    };
    const baseClass =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    return `<span class="${baseClass} ${classes[status] || classes.pending}">${labels[status] || status}</span>`;
  }

  getStepIcon(step, currentStep) {
    if (step === currentStep) {
      return '<span class="text-blue-500">&#8987;</span>'; // Hourglass
    }
    return '<span class="text-green-500">&#10003;</span>'; // Checkmark
  }
}
